import { deepClone } from "@medplum/core";
import type { Condition, Encounter, EpisodeOfCare } from "@medplum/fhirtypes";
import {
  CASE_ENTITY_ID_SYSTEM,
  CONDITION_ENTITY_ID_SYSTEM,
  getTRPCClient,
  PRACTITIONER_ENTITY_ID_SYSTEM,
} from "@repo/api";
import {
  type AddCaseDiagnosisInput,
  type AssignCareManagerInput,
  addCaseDiagnosisInputSchema,
  assignCareManagerInputSchema,
  caseIdSchema,
  conditionIdSchema,
  type LinkEncounterToCaseInput,
  linkEncounterToCaseInputSchema,
  patientIdSchema,
  practitionerIdSchema,
} from "@repo/api/schemas";
import type { CaseId, CaseStatus, ConditionId, PatientId, PractitionerId } from "@repo/domain";
import { ACQUISITION_SOURCES, type CaseAcquisition } from "@repo/domain";
import {
  CONDITION_CATEGORY_SYSTEM as CATEGORY_SYSTEM,
  CONDITION_CLINICAL_SYSTEM as CLINICAL_SYSTEM,
  CONDITION_VERIFICATION_SYSTEM as VER_SYSTEM,
} from "@repo/fhir-systems";
import { CASE_ACQUISITION_SYSTEM } from "@repo/fhir-systems/code-systems";
import { eq, inArray, useLiveQuery } from "@tanstack/react-db";
import { defineWrite } from "../actions/define-write";
import { useCollection } from "../core/hooks";
import { getDataStoreClient } from "../core/provider";
import { DomainResultError } from "../core/write-errors";
import type { AppointmentRow, EncounterDerived, EpisodeOfCareRow } from "../fhir/types";
import { optimisticCaseReference } from "./_case-reference";
import { patientDomainId } from "./_patient-reference";
import type { CodeableConcept, Period } from "./shared/types";

/**
 * View-owned case summary for provider case navigation and curation readiness.
 */
export interface CaseDto {
  acquisition?: CaseAcquisition;
  caseId: CaseId;
  patientId: PatientId;

  status: CaseStatus;

  type?: CodeableConcept[];
  period?: Period;

  careManagerPractitionerId?: PractitionerId;

  /** Condition ids carried on EoC.diagnosis[].condition — patient's working problem list for this episode. */
  diagnosis?: ConditionId[];
}

const EPISODE_OF_CARE_COLLECTION = "episodeOfCare";

const VALID_STATUS: ReadonlySet<string> = new Set(["active", "finished", "cancelled", "entered-in-error"]);

function flatCodeable(
  c: { coding?: Array<{ system?: string; code?: string; display?: string }>; text?: string } | undefined,
): CodeableConcept | undefined {
  if (!c) return undefined;
  const first = c.coding?.[0];
  const out = {
    ...(first?.system ? { system: first.system } : {}),
    ...(first?.code ? { code: first.code } : {}),
    ...(first?.display ? { display: first.display } : {}),
    ...(c.text ? { text: c.text } : {}),
  };
  return Object.keys(out).length ? out : undefined;
}

function flatCodeableArray(values: EpisodeOfCare["type"] | undefined): CodeableConcept[] | undefined {
  if (!values?.length) return undefined;
  const mapped = values.map(flatCodeable).filter((c): c is CodeableConcept => !!c);
  return mapped.length ? mapped : undefined;
}

function flatPeriod(p: { start?: string; end?: string } | undefined): Period | undefined {
  if (!p) return undefined;
  const out = {
    ...(p.start ? { start: p.start } : {}),
    ...(p.end ? { end: p.end } : {}),
  };
  return Object.keys(out).length ? out : undefined;
}

function diagnosisIds(diagnosis: EpisodeOfCare["diagnosis"]): ConditionId[] | undefined {
  if (!diagnosis?.length) return undefined;
  const ids = diagnosis.flatMap((diagnosis) => {
    const identifier = diagnosis.condition?.identifier;
    if (identifier?.system !== CONDITION_ENTITY_ID_SYSTEM || !identifier.value) return [];
    const parsed = conditionIdSchema.safeParse(identifier.value);
    return parsed.success ? [parsed.data] : [];
  });
  return ids.length ? ids : undefined;
}

function careManagerDomainId(ep: EpisodeOfCareRow): string | undefined {
  if (ep.careManager?.identifier?.system === PRACTITIONER_ENTITY_ID_SYSTEM && ep.careManager.identifier.value) {
    return ep.careManager.identifier.value;
  }
  return undefined;
}

/**
 * `undefined` for a row whose identity cannot be resolved YET, which is not the
 * same as a row that is wrong.
 *
 * A live query runs on every collection change, so it sees the moments between
 * them: EpisodeOfCare syncs before Patient, the left join finds nothing, and the
 * joined Patient has no domain id to offer. Throwing there — which
 * `patientIdSchema.parse("")` did — escapes the query into React and blanks the
 * tree permanently over a window that resolves milliseconds later. Omitting the
 * row costs one render in which the case is missing, and the query puts it back.
 */
function toDto(ep: EpisodeOfCareRow): CaseDto | undefined {
  // Match the mapper for episodes saved before cases began opening active.
  const status = ep.status === "planned" || ep.status === "waitlist" || ep.status === "onhold" ? "active" : ep.status;
  if (!status || !VALID_STATUS.has(status)) return undefined;
  const careManagerId = careManagerDomainId(ep);
  const caseId = caseIdSchema.safeParse(ep.domainId);
  const patientId = patientIdSchema.safeParse(patientDomainId(ep.patient));
  if (!caseId.success || !patientId.success) return undefined;
  // The care manager is optional, so an unresolvable one drops the FIELD rather
  // than the case: a case with no clinician named still reads correctly.
  const careManager = careManagerId ? practitionerIdSchema.safeParse(careManagerId) : undefined;
  return {
    caseId: caseId.data,
    patientId: patientId.data,
    status: status as CaseDto["status"],
    ...(caseAcquisition(ep) ? { acquisition: caseAcquisition(ep) } : {}),
    ...(flatCodeableArray(ep.type) ? { type: flatCodeableArray(ep.type) } : {}),
    ...(flatPeriod(ep.period) ? { period: flatPeriod(ep.period) } : {}),
    ...(careManager?.success ? { careManagerPractitionerId: careManager.data } : {}),
    ...(diagnosisIds(ep.diagnosis) ? { diagnosis: diagnosisIds(ep.diagnosis) } : {}),
  };
}

export interface CaseFilters {
  ids?: readonly string[];
  patientId?: string;
  status?: CaseDto["status"];
}

export function useCases(params: CaseFilters = {}): CaseDto[] {
  const collection = useCollection(EPISODE_OF_CARE_COLLECTION);
  const { ids, patientId, status } = params;
  const result = useLiveQuery({
    queryKey: [collection.id, "cases", ids ?? null, patientId ?? null, status ?? null],
    query: (q) => {
      if (ids?.length === 0) return undefined;
      let query = q.from({ episode: collection });
      if (ids) query = query.where(({ episode }) => inArray(episode.domainId, [...ids]));
      if (patientId) query = query.where(({ episode }) => eq(episode.patientId, patientId));
      if (status !== undefined) query = query.where(({ episode }) => eq(episode.status, status));
      // Nested FHIR reference/coding arrays cannot be flattened with standard
      // scalar expressions, so the functional projection is the narrow escape.
      // Wrapped in `{ dto }` because a projection cannot yield nothing —
      // dropping the unresolved row is the hook's job, as in allergy-intolerance.
      return query.fn.select(({ episode }) => ({ dto: toDto(episode) }));
    },
  });
  return ((result.data as Array<{ dto?: CaseDto }> | undefined) ?? []).flatMap((row) => (row.dto ? [row.dto] : []));
}

export function useCase(caseId: string | undefined): CaseDto | undefined {
  const collection = useCollection(EPISODE_OF_CARE_COLLECTION);
  const result = useLiveQuery({
    queryKey: [collection.id, "case", caseId ?? null],
    query: (q) => {
      if (!caseId) return undefined;
      return (
        q
          .from({ episode: collection })
          .where(({ episode }) => eq(episode.domainId, caseId))
          // Nested FHIR reference/coding arrays require the functional projection.
          .fn.select(({ episode }) => ({ dto: toDto(episode) }))
          .findOne()
      );
    },
  });
  return (result.data as { dto?: CaseDto } | undefined)?.dto;
}

const episodes = () => getDataStoreClient().getCollection("episodeOfCare");
const conditions = () => getDataStoreClient().getCollection("condition");
const appointments = () => getDataStoreClient().getCollection<AppointmentRow, string>("appointment");
const encounters = () => getDataStoreClient().getCollection<Encounter, string>("encounter");

/** Rows are keyed by domain identity; this only asserts the row is loaded. */
function resourceIdForDomainId(caseId: string): string {
  if (!episodes().has(caseId)) throw new Error(`Case '${caseId}' is not loaded`);
  return caseId;
}

/** Rows are keyed by domain identity; this only asserts the row is loaded. */
function visitResourceId(visitId: string): string {
  if (!appointments().has(visitId)) throw new Error(`Visit '${visitId}' is not loaded`);
  return visitId;
}

/** Intent: associate a checked-in visit's Encounter with an open case. */
export const linkEncounterToCase = defineWrite<LinkEncounterToCaseInput>({
  optimistic: (params) => {
    const input = linkEncounterToCaseInputSchema.parse(params);
    const caseReference = optimisticCaseReference(input.caseId);
    appointments().update(visitResourceId(input.visitId), (draft) => {
      const supporting = (draft.supportingInformation ?? []).filter(
        (reference) => reference.identifier?.system !== CASE_ENTITY_ID_SYSTEM,
      );
      draft.supportingInformation = [...supporting, caseReference];
    });
    for (const encounter of encounters().values()) {
      if ((encounter as Partial<EncounterDerived>).visitId !== input.visitId) continue;
      encounters().update(encounters().getKeyFromItem(encounter), (draft) => {
        draft.episodeOfCare = [caseReference];
      });
    }
  },
  persist: async (params, options) => {
    const result = await getTRPCClient().case.linkEncounter.mutate(params, options);
    if (!result.success) throw new DomainResultError(result.error.message);
  },
});

/**
 * Intent: "add a diagnosis to a case" — creates a problem-list Condition for the
 * patient and appends it to EpisodeOfCare.diagnosis[].
 */
export const addCaseDiagnosis = defineWrite<AddCaseDiagnosisInput>({
  optimistic: (params) => {
    const input = addCaseDiagnosisInputSchema.parse(params);
    const caseResourceId = resourceIdForDomainId(input.caseId);
    const ep = episodes().get(caseResourceId) as EpisodeOfCare | undefined;
    const patient = ep?.patient;
    if (!patient) return;

    const optimisticConditionId = input.id;
    conditions().insert({
      resourceType: "Condition",
      identifier: [{ system: CONDITION_ENTITY_ID_SYSTEM, value: optimisticConditionId }],
      domainId: optimisticConditionId,
      subject: deepClone(patient),
      code: input.code,
      category: [{ coding: [{ system: CATEGORY_SYSTEM, code: "problem-list-item" }] }],
      clinicalStatus: { coding: [{ system: CLINICAL_SYSTEM, code: input.clinicalStatus ?? "active" }] },
      verificationStatus: { coding: [{ system: VER_SYSTEM, code: input.verificationStatus ?? "confirmed" }] },
      severity: input.severity,
      recordedDate: new Date().toISOString(),
      onsetDateTime: input.onsetDateTime,
      note: input.note ? [{ text: input.note }] : undefined,
    } as Condition);

    episodes().update(caseResourceId, (draft) => {
      draft.diagnosis = [
        ...(draft.diagnosis ?? []),
        {
          condition: { identifier: { system: CONDITION_ENTITY_ID_SYSTEM, value: optimisticConditionId } },
        },
      ];
    });
  },
  persist: async (params, options) => {
    const result = await getTRPCClient().case.addDiagnosis.mutate(params, options);
    if (!result.success) throw new DomainResultError(result.error.message);
  },
});

/**
 * Intent: "assign the case's care manager" — sets EoC.careManager. Bookings minted
 * off this case's curated plan default their practitioner to this provider.
 */
export const assignCareManager = defineWrite<AssignCareManagerInput>({
  optimistic: (params) => {
    const input = assignCareManagerInputSchema.parse(params);
    const collection = episodes();
    const caseResourceId = resourceIdForDomainId(input.caseId);
    // Client-local row: identifier-only, so the identifier-first reader resolves it.
    // The server mapper re-derives the literal locator on the authoritative write.
    const nextCareManager = {
      identifier: { system: PRACTITIONER_ENTITY_ID_SYSTEM, value: input.careManager.id },
    };
    collection.update(caseResourceId, (draft) => {
      draft.careManager = nextCareManager;
    });
  },
  persist: async (params, options) => {
    const result = await getTRPCClient().case.assignCareManager.mutate(params, options);
    if (!result.success) throw new DomainResultError(result.error.message);
  },
});

function caseAcquisition(episode: EpisodeOfCare): CaseAcquisition | undefined {
  const code = episode.type
    ?.flatMap((type) => type.coding ?? [])
    .find((coding) => coding.system === CASE_ACQUISITION_SYSTEM)?.code;
  const source = ACQUISITION_SOURCES.find((source) => source === code);
  if (!source) return undefined;
  return { source };
}
